import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const WebSocketContext = createContext({
    lastOrders: [],
    productsOrders: {},
    isConnected: false,
});

export const useWebSocket = () => useContext(WebSocketContext);

const formatWallet = (wallet) => {
    if (!wallet) return 'Anonymous';
    if (wallet.length <= 10) return wallet;
    return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
};

const formatDate = (timestamp) => {
    if (!timestamp) return new Date().toLocaleString();
    const date = new Date(timestamp);
    return date.toLocaleString();
};

const formatAmount = (amount) => {
    const val = parseFloat(amount);
    return isNaN(val) ? '0.00' : val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCurrency = (currency) => {
    if (!currency) return 'usdt';
    // Take the first part before any hyphen or space, e.g., "usdt-erc20" -> "usdt"
    return currency.split(/[- ]/)[0].toLowerCase();
};

export const WebSocketProvider = ({ children }) => {
    const [lastOrders, setLastOrders] = useState([]);
    const [productsOrders, setProductsOrders] = useState({});
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    const connect = useCallback(() => {
        const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080';
        const wsUrl = baseUrl.replace(/^http/, 'ws') + '/ws/recent-orders';

        console.log(`[WebSocket] Connecting to ${wsUrl}...`);
        
        const socket = new WebSocket(wsUrl);
        socketRef.current = socket;

        socket.onopen = () => {
            console.log('[WebSocket] Connected');
            setIsConnected(true);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // If the message contains 'last_orders' and 'products' (initial burst)
                if (data.last_orders && data.products) {
                    const formattedLastOrders = data.last_orders.map(order => ({
                        ...order,
                        wallet: formatWallet(order.wallet),
                        date: formatDate(order.trx_date),
                        amount: formatAmount(order.amount_in_usd),
                        currency: formatCurrency(order.currency)
                    }));
                    
                    const formattedProducts = {};
                    Object.keys(data.products).forEach(key => {
                        formattedProducts[key.trim()] = data.products[key].map(order => ({
                            ...order,
                            wallet: formatWallet(order.wallet),
                            date: formatDate(order.trx_date),
                            amount: formatAmount(order.amount_in_usd),
                            currency: formatCurrency(order.currency)
                        }));
                    });

                    setLastOrders(formattedLastOrders);
                    setProductsOrders(formattedProducts);
                } else if (data.wallet) {
                    // It's a single update
                    const newOrder = {
                        ...data,
                        wallet: formatWallet(data.wallet),
                        date: formatDate(data.trx_date),
                        amount: formatAmount(data.amount_in_usd),
                        currency: formatCurrency(data.currency)
                    };

                    setLastOrders(prev => [newOrder, ...prev]);
                    
                    if (data.product_name) {
                        const productName = data.product_name.trim();
                        setProductsOrders(prev => ({
                            ...prev,
                            [productName]: [newOrder, ...(prev[productName] || [])]
                        }));
                    }
                }
            } catch (err) {
                console.error('[WebSocket] Failed to parse message:', err);
            }
        };

        socket.onerror = (error) => {
            console.error('[WebSocket] Error:', error);
            setIsConnected(false);
        };

        socket.onclose = () => {
            console.log('[WebSocket] Disconnected. Reconnecting in 3s...');
            setIsConnected(false);
            reconnectTimeoutRef.current = setTimeout(connect, 3000);
        };
    }, []);

    useEffect(() => {
        connect();
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [connect]);

    return (
        <WebSocketContext.Provider value={{ lastOrders, productsOrders, isConnected }}>
            {children}
        </WebSocketContext.Provider>
    );
};
